import React, { useEffect, useMemo, useState } from 'react';
import { KPICards } from '../components/KPICards';
import { CampaignOverviewChart } from '../components/CampaignOverviewChart';
import { CampaignGrid } from '../components/CampaignGrid';
import { allCampaigns, brandModels } from '../data/mockData';
import { formatCurrency, formatNumber } from '../lib/format';
import { useOutletContext } from 'react-router-dom';
export type ChartMetric = 'budget' | 'leads' | 'cpc';
interface CampaignContext {
  activeBrand: string;
  activeMarkets: string[];
  activeModel: string;
  activeType: string | null;
  activeYears: string[];
}
export function CampaignPage() {
  const context = useOutletContext<CampaignContext>();
  const {
    activeBrand,
    activeMarkets,
    activeModel,
    activeType,
    activeYears
  } = context;
  const [chartMetric, setChartMetric] = useState<ChartMetric>('budget');
  const filteredCampaigns = useMemo(() => {
    return allCampaigns.filter(campaign => {
      if (campaign.brand !== activeBrand) return false;
      if (campaign.model !== activeModel) return false;
      if (activeType && campaign.type !== activeType) return false;
      if (activeYears.length > 0 && !activeYears.includes(campaign.year)) return false;
      if (activeMarkets.length > 0 && !activeMarkets.includes(campaign.market)) return false;
      return true;
    });
  }, [activeBrand, activeModel, activeType, activeYears, activeMarkets]);
  const campaignKpiData = useMemo(() => {
    let totalBudget = 0;
    let totalLeads = 0;
    filteredCampaigns.forEach(c => {
      totalBudget += c.metrics.rawBudget;
      totalLeads += c.metrics.rawLeads;
    });
    const costPerLead = totalLeads > 0 ? totalBudget / totalLeads : 0;
    return {
      totalCampaigns: filteredCampaigns.length,
      budget: formatCurrency(totalBudget),
      leads: formatNumber(totalLeads),
      costPerLead: formatCurrency(costPerLead)
    };
  }, [filteredCampaigns]);
  const getMetricValue = (campaign: (typeof filteredCampaigns)[0]) => {
    switch (chartMetric) {
      case 'budget':
        return campaign.metrics.rawBudget;
      case 'leads':
        return campaign.metrics.rawLeads;
      case 'cpc':
        return campaign.metrics.rawClicks > 0 ? Math.round(campaign.metrics.rawBudget / campaign.metrics.rawClicks * 100) / 100 : 0;
    }
  };
  const chartData = useMemo(() => {
    const years = ['2023', '2024', '2025', '2026'];
    const sorted = [...filteredCampaigns].sort((a, b) => {
      if (a.year !== b.year) return a.year.localeCompare(b.year);
      return a.date.localeCompare(b.date);
    });
    const brandColors = ['#1e293b', '#334155', '#475569', '#0f172a'];
    const tacticalColors = ['#ef4444', '#f97316', '#ec4899', '#e11d48'];
    const yearTypeCounters: Record<string, number> = {};
    const datasets = sorted.map(campaign => {
      const yearIndex = years.indexOf(campaign.year);
      const value = getMetricValue(campaign);
      const data = years.map((_, i) => i === yearIndex ? value : null);
      const key = `${campaign.year}-${campaign.type}`;
      const idx = yearTypeCounters[key] || 0;
      yearTypeCounters[key] = idx + 1;
      const palette = campaign.type === 'Brand' ? brandColors : tacticalColors;
      const color = palette[idx % palette.length];
      return {
        label: campaign.title,
        data,
        backgroundColor: color,
        borderRadius: 6,
        barPercentage: 0.8,
        categoryPercentage: 0.85
      };
    });
    return {
      labels: years,
      datasets
    };
  }, [filteredCampaigns, chartMetric]);
  return <>
      <KPICards data={campaignKpiData} data-id="element-200" />
      <CampaignOverviewChart data={chartData} chartMetric={chartMetric} setChartMetric={setChartMetric} data-id="element-201" />
      <CampaignGrid campaigns={filteredCampaigns} data-id="element-202" />
    </>;
}